/**
 * CustomGrid Component - A modern, reusable HTML-based grid with filtering and sorting
 * 
 * Features:
 * - Clean modern UI design
 * - Filter dialog popup (not column-level)
 * - Sort icons on header hover
 * - Click header to sort
 * - Remote filtering support
 * - Custom cell rendering
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tooltip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem, Checkbox, TextField, Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SaveIcon from '@mui/icons-material/Save';
import './CustomGrid.scss';

// Filter operator definitions
const FILTER_OPERATORS = {
    string: [
        { value: 'contains', label: 'contains' },
        { value: 'equals', label: 'equals' },
        { value: 'startsWith', label: 'starts with' },
        { value: 'endsWith', label: 'ends with' }
    ],
    singleSelect: [
        { value: 'equals', label: 'equals' }
    ],
    number: [
        { value: '=', label: '=' },
        { value: '>', label: '>' },
        { value: '<', label: '<' },
        { value: '>=', label: '>=' },
        { value: '<=', label: '<=' }
    ],
    date: [
        { value: 'is', label: 'is' },
        { value: 'after', label: 'after' },
        { value: 'before', label: 'before' }
    ]
};

// Get operators based on column type
const getOperatorsForType = (type) => {
    if (type === 'singleSelect') return FILTER_OPERATORS.singleSelect;
    if (type === 'number') return FILTER_OPERATORS.number;
    if (type === 'date') return FILTER_OPERATORS.date;
    return FILTER_OPERATORS.string;
};

// Apply filter logic
const applyFilter = (value, filterValue, operator, type) => {
    if (filterValue === '' || filterValue === null || filterValue === undefined) return true;
    
    // Handle multi-select array filter values
    if (Array.isArray(filterValue)) {
        if (filterValue.length === 0 || filterValue.includes('All')) return true;
        return filterValue.some(fv => String(value || '').toLowerCase() === String(fv).toLowerCase());
    }

    const strValue = String(value || '').toLowerCase();
    const strFilter = String(filterValue).toLowerCase();
    
    if (type === 'number') {
        const numValue = parseFloat(value);
        const numFilter = parseFloat(filterValue);
        if (isNaN(numValue) || isNaN(numFilter)) return true;
        
        switch (operator) {
            case '=': return numValue === numFilter;
            case '>': return numValue > numFilter;
            case '<': return numValue < numFilter;
            case '>=': return numValue >= numFilter;
            case '<=': return numValue <= numFilter;
            default: return true;
        }
    }
    
    if (type === 'date') {
        const dateValue = new Date(value);
        const dateFilter = new Date(filterValue);
        if (isNaN(dateValue.getTime()) || isNaN(dateFilter.getTime())) return true;
        
        switch (operator) {
            case 'is': return dateValue.toDateString() === dateFilter.toDateString();
            case 'after': return dateValue > dateFilter;
            case 'before': return dateValue < dateFilter;
            default: return true;
        }
    }
    
    // String/singleSelect types
    switch (operator) {
        case 'contains': return strValue.includes(strFilter);
        case 'equals': return strValue === strFilter;
        case 'startsWith': return strValue.startsWith(strFilter);
        case 'endsWith': return strValue.endsWith(strFilter);
        default: return strValue.includes(strFilter);
    }
};

// Filter Field Component
// Main CustomGrid Component
const AdvancedSearchDialog = ({
    open,
    onClose,
    fields = [],
    onApply,
    onReset,
    defaultValues = {}
}) => {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (open) {
            // Initialize form data with defaults
            const initialData = {};
            fields.forEach(field => {
                if (defaultValues[field.name] !== undefined) {
                    initialData[field.name] = defaultValues[field.name];
                } else if (field.isMulti && field.defaultOption) {
                    initialData[field.name] = [field.defaultOption];
                } else if (field.defaultOption) {
                    initialData[field.name] = field.defaultOption;
                }
            });
            setFormData(initialData);
        }
    }, [open, fields, defaultValues]);

    const handleFieldChange = (field, value) => {
        if (field.isMulti) {
            const existingSelection = formData[field.name] || [];
            const newSelection = value.filter(v => !existingSelection.includes(v));
            if (newSelection.includes("All")) {
                value = ["All"];
            } else if (existingSelection.includes("All") && newSelection.length > 0) {
                if (field.replaceAllSelectionOnClick) {
                    // Replace the select-all state with the option the user just selected.
                    value = newSelection;
                } else {
                    // Keep the existing exclusion behavior for other grids.
                    const allOptions = field.options?.filter(o => o !== "All") || [];
                    value = allOptions.filter(o => !newSelection.includes(o));
                }
            }
            // Auto-select "All" when all individual options are selected
            const allOptions = field.options?.filter(o => o !== "All") || [];
            const selectedNonAll = value.filter(v => v !== "All");
            if (allOptions.length > 0 && selectedNonAll.length === allOptions.length) {
                value = ["All"];
            }
        }
        setFormData(prev => ({
            ...prev,
            [field.name]: value
        }));
    };

    const isOptionDisabled = (optionName, field) => {
        return false;
    };

    const handleApply = () => {
        onApply(formData);
        onClose();
    };

    // Validation: multiselect fields with isMulti must have at least one option selected
    const emptyMultiselects = fields.filter(f => 
        f.type === 'multiselect' && f.isMulti && 
        (!formData[f.name] || formData[f.name].length === 0)
    ).map(f => f.name);
    const hasValidationErrors = emptyMultiselects.length > 0;

    const handleReset = () => {
        const resetData = {};
        fields.forEach(field => {
            if (field.isMulti && field.defaultOption) {
                resetData[field.name] = [field.defaultOption];
            } else if (field.defaultOption) {
                resetData[field.name] = field.defaultOption;
            }
        });
        setFormData(resetData);
        onApply(resetData);
        onClose();
    };

    const renderField = (field) => {
        if (field.type === 'text' || field.type === 'number') {
            return (
                <Grid item xs={3} key={field.name}>
                    <div className="cgrid-adv-field">
                        <label>{field.label}</label>
                        <TextField
                            size="small"
                            type={field.type}
                            fullWidth
                            variant="outlined"
                            value={formData[field.name] || ''}
                            onChange={(e) => handleFieldChange(field, field.enableTrim ? e.target.value.trim() : e.target.value)}
                            onKeyDown={(e) => {
                                if (field.handleEnterToSubmit && e.key === 'Enter') {
                                    handleApply();
                                }
                            }}
                        />
                    </div>
                </Grid>
            );
        }

        if (field.type === 'multiselect') {
            const hasError = field.isMulti && emptyMultiselects.includes(field.name);
            return (
                <Grid item xs={3} key={field.name}>
                    <div className="cgrid-adv-field">
                        <label>{field.label}</label>
                        <FormControl fullWidth variant="outlined" size="small" error={hasError}>
                            <Select
                                multiple={field.isMulti}
                                value={formData[field.name] || (field.isMulti ? [] : '')}
                                onChange={(e) => {
                                    if (field.isMulti) {
                                        handleFieldChange(field, e.target.value);
                                    }
                                }}
                                renderValue={(selected) => {
                                    if (field.isMulti) {
                                        return Array.isArray(selected) ? selected.join(', ') : selected;
                                    }
                                    return selected;
                                }}
                            >
                                {field.options?.map((option) => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                        disabled={isOptionDisabled(option, field)}
                                        onClick={(e) => {
                                            if (!field.isMulti) {
                                                e.stopPropagation();
                                                const currentValue = formData[field.name];
                                                const newValue = currentValue === option ? '' : option;
                                                handleFieldChange(field, newValue);
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            checked={field.isMulti 
                                                ? (formData[field.name]?.includes("All") || formData[field.name]?.includes(option))
                                                : formData[field.name] === option}
                                            disabled={isOptionDisabled(option, field)}
                                        />
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {hasError && (
                            <p className="cgrid-field-error">Please select an option</p>
                        )}
                        {!hasError && field.isMulti && formData[field.name]?.length > 1 && (
                            <p className="cgrid-multi-selected">{formData[field.name].join(', ')}</p>
                        )}
                    </div>
                </Grid>
            );
        }

        if (field.type === 'date') {
            return (
                <Grid item xs={3} key={field.name}>
                    <div className="cgrid-adv-field">
                        <label>{field.label}</label>
                        <TextField
                            size="small"
                            type="date"
                            fullWidth
                            variant="outlined"
                            value={formData[field.name] || ''}
                            onChange={(e) => handleFieldChange(field, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </Grid>
            );
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            className="cgrid-adv-search-dialog"
        >
            <DialogTitle className="cgrid-adv-search-title">
                <span>Set Filters</span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className="cgrid-adv-search-content">
                <Grid container spacing={2}>
                    {fields.map(renderField)}
                </Grid>
            </DialogContent>
            <DialogActions className="cgrid-adv-search-actions">
                <Button
                    onClick={onClose}
                    className="cgrid-btn cgrid-btn-cancel"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleReset}
                    className="cgrid-btn cgrid-btn-reset"
                >
                    Reset
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    className="cgrid-btn cgrid-btn-apply"
                    disabled={hasValidationErrors}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main CustomGrid Component
const CustomGrid = ({
    rows = [],
    columns = [],
    onCellClick,
    getCellClassName,
    getRowClassName,
    remoteFiltering = false,
    applyRemoteFiltering,
    applyRemoteSorting,
    defaultFilters = {},
    defaultSortModel = [],
    customHeight = 'calc(100vh - 350px)',
    enableCustomFiltering = true,
    showFilterButton = true,
    // Title prop
    title = '',
    // Download props
    onDownload,
    showDownloadButton = false,
    downloadDisabled = false,
    downloadDisabledTooltip = '',
    // Pagination props
    showPagination = false,
    currentPage = 1,
    pageCount = 20,
    totalResults = 0,
    onPageChange,
    onPageCountChange,
    pageSizeOptions = [20, 50, 100],
    // Advanced Search props
    advancedSearchFields = [],
    onAdvancedSearchApply,
    showAdvancedSearch = false,
    defaultAdvancedSearchValues = {},
    onResetAllFilters,
    activeRowId,
    // Column selector props
    showColumnSelector = false,
    // User preferences props
    onSaveDefaults,
    defaultHiddenColumns = {}
}) => {
    const [filters, setFilters] = useState(defaultFilters);
    const [sortModel, setSortModel] = useState(defaultSortModel);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(showAdvancedSearch);
    const [hoveredColumn, setHoveredColumn] = useState(null);
    const [columnWidths, setColumnWidths] = useState({});
    const resizingRef = useRef(null);
    const [gotoPageValue, setGotoPageValue] = useState('');
    const [gotoPageError, setGotoPageError] = useState(false);
    const [hiddenColumns, setHiddenColumns] = useState(defaultHiddenColumns);
    const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
    const columnSelectorRef = useRef(null);

    // Compute visible columns based on hiddenColumns state
    const visibleColumns = useMemo(() => {
        if (!showColumnSelector) return columns;
        return columns.filter(col => !hiddenColumns[col.mapping]);
    }, [columns, hiddenColumns, showColumnSelector]);

    // Auto-generate advancedSearchFields from columns when none are provided
    const autoGeneratedFields = useMemo(() => {
        if (advancedSearchFields.length > 0) return null;
        const filterableColumns = columns.filter(c => c.mapping !== 'id' && !c.disableColumnMenu);
        return filterableColumns.map(col => {
            if (col.type === 'singleSelect' && col.valueOptions) {
                return {
                    type: 'multiselect',
                    name: col.mapping,
                    label: col.name,
                    isMulti: true,
                    options: ['All', ...col.valueOptions],
                    defaultOption: 'All',
                    replaceAllSelectionOnClick: col.replaceAllSelectionOnClick,
                };
            }
            if (col.type === 'date') {
                return { type: 'date', name: col.mapping, label: col.name };
            }
            if (col.type === 'number') {
                return { type: 'number', name: col.mapping, label: col.name };
            }
            return { type: 'text', name: col.mapping, label: col.name };
        });
    }, [advancedSearchFields, columns]);

    // Track auto-generated filter values (used when no advancedSearchFields provided)
    const [autoFilterValues, setAutoFilterValues] = useState({});

    // Effective fields and handlers for the AdvancedSearchDialog
    const effectiveFields = autoGeneratedFields || advancedSearchFields;
    const isAutoGenerated = autoGeneratedFields !== null;

    // Close column selector dropdown on outside click
    useEffect(() => {
        if (!columnSelectorOpen) return;
        const handleClickOutside = (e) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(e.target)) {
                setColumnSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [columnSelectorOpen]);

    // Sync advanced search dialog with prop
    useEffect(() => {
        setAdvancedSearchOpen(showAdvancedSearch);
    }, [showAdvancedSearch]);

    // Sync internal state with external default values from parent
    useEffect(() => {
        setFilters(defaultFilters);
    }, [JSON.stringify(defaultFilters)]);

    // Keep auto-generated filter dialog values in sync with active filters
    // so saved user-preference filters are shown in the Set Filters dialog.
    useEffect(() => {
        if (!isAutoGenerated) return;
        const syncedValues = {};
        Object.keys(defaultFilters || {}).forEach((key) => {
            const filter = defaultFilters[key];
            if (!filter || filter.value === undefined || filter.value === null) return;
            syncedValues[key] = filter.value;
        });
        setAutoFilterValues(syncedValues);
    }, [isAutoGenerated, JSON.stringify(defaultFilters)]);

    useEffect(() => {
        setSortModel(defaultSortModel);
    }, [JSON.stringify(defaultSortModel)]);

    // Sync hidden columns from saved preferences
    useEffect(() => {
        setHiddenColumns(defaultHiddenColumns);
    }, [JSON.stringify(defaultHiddenColumns)]);

    // Notify parent when filters change — skip if value matches parent defaults (prevents loop)
    useEffect(() => {
        if (JSON.stringify(filters) === JSON.stringify(defaultFilters)) return;
        if (remoteFiltering && applyRemoteFiltering) {
            applyRemoteFiltering(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters)]);

    // Notify parent when sort changes — skip if value matches parent defaults (prevents loop)
    useEffect(() => {
        if (JSON.stringify(sortModel) === JSON.stringify(defaultSortModel)) return;
        if (applyRemoteSorting) {
            applyRemoteSorting(sortModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(sortModel)]);

    // Handle filter apply
    const handleFiltersApply = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    // Handle auto-generated filter apply (converts AdvancedSearchDialog formData to filters format)
    const handleAutoFilterApply = useCallback((formData) => {
        setAutoFilterValues(formData);
        const formatted = {};
        Object.keys(formData).forEach(key => {
            const value = formData[key];
            if (!value || value === '' || (Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === 'All')))) {
                return; // skip empty / "All" values
            }
            const column = columns.find(c => c.mapping === key);
            const operators = getOperatorsForType(column?.type);
            if (Array.isArray(value)) {
                const filtered = value.filter(v => v !== 'All');
                if (filtered.length > 0) {
                    formatted[key] = { value: filtered, operatorValue: operators[0]?.value || 'contains', columnInfo: column };
                }
            } else {
                formatted[key] = { value, operatorValue: operators[0]?.value || 'contains', columnInfo: column };
            }
        });
        setFilters(formatted);
    }, [columns]);

    // Handle filter reset
    const handleFiltersReset = useCallback(() => {
        setFilters({});
        setAutoFilterValues({});
        if (remoteFiltering && applyRemoteFiltering) {
            applyRemoteFiltering({});
        }
    }, [remoteFiltering, applyRemoteFiltering]);

    const handleResetAllFilters = useCallback(() => {
        setFilters({});
        setAutoFilterValues({});
        if (onResetAllFilters) {
            onResetAllFilters();
        }
        setAdvancedSearchOpen(false);
    }, [onResetAllFilters]);

    // Column resize handlers
    const handleResizeStart = useCallback((e, column) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = columnWidths[column.mapping] || column.width || 100;
        
        resizingRef.current = {
            column: column.mapping,
            startX,
            startWidth
        };

        const handleMouseMove = (moveEvent) => {
            if (!resizingRef.current) return;
            const { column: activeColumn, startX: initialX, startWidth: initialWidth } = resizingRef.current;
            const diff = moveEvent.clientX - initialX;
            const newWidth = Math.max(50, initialWidth + diff);
            setColumnWidths(prev => ({
                ...prev,
                [activeColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            resizingRef.current = null;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [columnWidths]);

    // Get effective column width (resized or default)
    const getColumnWidth = useCallback((column) => {
        return columnWidths[column.mapping] || column.width || 'auto';
    }, [columnWidths]);

    // Handle sort on header click
    const handleSort = useCallback((column) => {
        if (column.sortable === false) return;
        
        setSortModel(prev => {
            const existingSort = prev.find(s => s.field === column.mapping);
            if (existingSort) {
                if (existingSort.sort === 'asc') {
                    return [{ field: column.mapping, sort: 'desc' }];
                }
                return []; // Remove sort
            }
            return [{ field: column.mapping, sort: 'asc' }];
        });
    }, []);

    // Get filtered and sorted rows
    const processedRows = useMemo(() => {
        let result = [...rows];
        
        // Apply local filtering if not remote
        if (!remoteFiltering) {
            Object.keys(filters).forEach(field => {
                const filter = filters[field];
                if (filter && filter.value) {
                    const column = columns.find(c => c.mapping === field);
                    result = result.filter(row => {
                        if (column?.filterMatcher) {
                            return column.filterMatcher(row, filter.value, filter.operatorValue);
                        }
                        const value = row[field];
                        return applyFilter(value, filter.value, filter.operatorValue, column?.type);
                    });
                }
            });
        }
        
        // Apply local sorting if not remote
        if (!applyRemoteSorting && sortModel.length > 0) {
            const sort = sortModel[0];
            result.sort((a, b) => {
                const aVal = a[sort.field];
                const bVal = b[sort.field];
                
                if (aVal === bVal) return 0;
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                
                const comparison = aVal < bVal ? -1 : 1;
                return sort.sort === 'asc' ? comparison : -comparison;
            });
        }
        
        return result;
    }, [rows, filters, sortModel, remoteFiltering, applyRemoteSorting, columns]);

    // Get sort direction for column
    const getSortDirection = useCallback((field) => {
        const sort = sortModel.find(s => s.field === field);
        return sort?.sort || null;
    }, [sortModel]);

    // Count active filters (including advanced search filters)
    const activeFilterCount = useMemo(() => {
        // Count column-based filters
        const columnFilterCount = Object.keys(filters).filter(key => filters[key]?.value).length;
        
        // Count advanced search filters
        let advancedFilterCount = 0;
        let hasDateFilter = false;
        
        Object.keys(defaultAdvancedSearchValues).forEach(key => {
            const value = defaultAdvancedSearchValues[key];
            if (value && value !== '' && !(Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === 'All')))) {
                // Count from_date and to_date as a single "date range" filter
                if (key === 'from_date' || key === 'to_date') {
                    hasDateFilter = true;
                } else {
                    advancedFilterCount++;
                }
            }
        });
        
        // Add 1 for date range if either from_date or to_date is set
        if (hasDateFilter) {
            advancedFilterCount++;
        }
        
        return columnFilterCount + advancedFilterCount;
    }, [filters, defaultAdvancedSearchValues]);

    // Render cell content
    const renderCell = useCallback((column, row, rowIndex) => {
        const params = {
            row,
            value: row[column.mapping],
            field: column.mapping,
            rowIndex
        };
        
        if (column.renderCell) {
            return column.renderCell(params);
        }
        
        if (column.valueGetter) {
            const value = column.valueGetter(params);
            return value instanceof Date ? value.toLocaleDateString() : value;
        }
        
        return row[column.mapping] ?? '--';
    }, []);

    // Get cell class name
    const getCellClass = useCallback((column, row) => {
        if (getCellClassName) {
            return getCellClassName({ field: column.mapping, value: row[column.mapping], row });
        }
        return '';
    }, [getCellClassName]);

    // Render sort icon - always render to prevent width change
    const renderSortIcon = (column) => {
        const sortDir = getSortDirection(column.mapping);
        const isHovered = hoveredColumn === column.mapping;
        const isSortable = column.sortable !== false;

        if (!isSortable) return null;

        if (sortDir === 'asc') {
            return <ArrowUpwardIcon className="cgrid-sort-icon active" />;
        }
        if (sortDir === 'desc') {
            return <ArrowDownwardIcon className="cgrid-sort-icon active" />;
        }
        // Always render the icon but control visibility with CSS
        return <UnfoldMoreIcon className={`cgrid-sort-icon placeholder ${isHovered ? 'visible' : ''}`} />;
    };

    // Go to page handlers
    const handleGotoPageChange = (e) => {
        const value = e.target.value;
        // Only allow numbers
        if (value === '' || /^\d+$/.test(value)) {
            setGotoPageValue(value);
            setGotoPageError(false);
        }
    };

    const handleGotoPageSubmit = (e) => {
        if (e.key === 'Enter' && gotoPageValue) {
            const pageNum = parseInt(gotoPageValue);
            const totalPages = Math.ceil(totalResults / pageCount);
            
            if (pageNum < 1 || pageNum > totalPages) {
                setGotoPageError(true);
                setTimeout(() => setGotoPageError(false), 3000);
            } else {
                setGotoPageError(false);
                onPageChange && onPageChange(pageNum);
                setGotoPageValue('');
            }
        }
    };

    // Check if a column has an active filter from advanced search
    const getColumnFilterInfo = useCallback((columnMapping) => {
        // Map column mapping to advanced search field name
        const fieldMappings = {
            'requestId': 'id',
            'ticket_type': 'ticket_type',
            'status': 'status',
            'requestor': 'requestor',
            'downstreamApps': 'app',
            'created_date': ['from_date', 'to_date']
        };

        const fieldName = fieldMappings[columnMapping];
        
        if (!fieldName) return null;

        // Handle date columns which map to from_date and to_date
        if (Array.isArray(fieldName)) {
            const dateFilters = [];
            fieldName.forEach(fn => {
                const value = defaultAdvancedSearchValues[fn];
                if (value && value !== '' && value !== 'All') {
                    const field = advancedSearchFields.find(f => f.name === fn);
                    dateFilters.push(`${field?.label || fn}: ${value}`);
                }
            });
            return dateFilters.length > 0 ? dateFilters.join('\n') : null;
        }

        const value = defaultAdvancedSearchValues[fieldName];
        if (!value || value === '' || (Array.isArray(value) && (value.length === 0 || (value.length === 1 && value[0] === 'All')))) {
            return null;
        }

        const field = advancedSearchFields.find(f => f.name === fieldName);
        const displayValue = Array.isArray(value) ? value.filter(v => v !== 'All').join(', ') : value;
        return `${field?.label || fieldName}: ${displayValue}`;
    }, [defaultAdvancedSearchValues, advancedSearchFields]);

    // Calculate frozen column left offsets
    const getFrozenColumnStyle = useMemo(() => {
        const offsets = {};
        const zIndexes = {};
        let currentOffset = 0;
        let frozenIndex = 0;
        const frozenCount = visibleColumns.filter(col => col.frozen).length;
        
        visibleColumns.forEach((col) => {
            if (col.frozen) {
                offsets[col.mapping] = currentOffset;
                zIndexes[col.mapping] = frozenCount - frozenIndex;
                currentOffset += (columnWidths[col.mapping] || col.width || 100);
                frozenIndex++;
            }
        });
        
        return (column, isHeader = false, isActiveRow = false) => {
            if (!column.frozen) return {};
            const baseZIndex = isHeader ? 100 : 50;
            let bgColor = isHeader ? '#dee2e6' : '#f5f7fa';
            if (!isHeader && isActiveRow) {
                bgColor = '#c3e6cb';
            }
            return {
                position: 'sticky',
                left: offsets[column.mapping],
                zIndex: baseZIndex + zIndexes[column.mapping],
                background: bgColor
            };
        };
    }, [visibleColumns, columnWidths]);

    return (
        <div className="cgrid-container" style={{ height: customHeight }}>
            {/* Toolbar */}
            {(showFilterButton || showDownloadButton || title || advancedSearchFields.length > 0 || showColumnSelector) && (
                <div className="cgrid-toolbar">
                   
                    {showDownloadButton && (
                        <Tooltip title={downloadDisabled ? downloadDisabledTooltip : "Download Search Results"}>
                            <span style={{ display: 'inline-flex', cursor: downloadDisabled ? 'not-allowed' : 'pointer' }}>
                                <button 
                                    className={`cgrid-toolbar-btn${downloadDisabled ? ' no-access' : ''}`}
                                    onClick={onDownload}
                                    disabled={downloadDisabled}
                                >
                                    <GetAppIcon />
                                </button>
                            </span>
                        </Tooltip>
                    )}
                    {showColumnSelector && (
                        <div className="cgrid-column-selector-wrapper" ref={columnSelectorRef}>
                            <Tooltip title="Show/Hide Columns">
                                <button
                                    className={`cgrid-toolbar-btn ${Object.values(hiddenColumns).some(v => v) ? 'active' : ''}`}
                                    onClick={() => setColumnSelectorOpen(prev => !prev)}
                                >
                                    <ViewColumnIcon />
                                    {Object.values(hiddenColumns).filter(v => v).length > 0 && (
                                        <span className="cgrid-filter-badge">
                                            {Object.values(hiddenColumns).filter(v => v).length}
                                        </span>
                                    )}
                                </button>
                            </Tooltip>
                            {columnSelectorOpen && (
                                <div className="cgrid-column-selector-dropdown">
                                    <div className="cgrid-column-selector-header">
                                        <span>Columns</span>
                                        <IconButton size="small" onClick={() => setColumnSelectorOpen(false)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                    <div className="cgrid-column-selector-list">
                                        {columns.filter(col => col.hideable !== false).map((col) => (
                                            <label key={col.mapping} className="cgrid-column-selector-item">
                                                <input
                                                    type="checkbox"
                                                    checked={!hiddenColumns[col.mapping]}
                                                    onChange={() => {
                                                        setHiddenColumns(prev => ({
                                                            ...prev,
                                                            [col.mapping]: !prev[col.mapping]
                                                        }));
                                                    }}
                                                />
                                                <span>{col.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="cgrid-column-selector-actions">
                                        <button
                                            className="cgrid-column-selector-show-all"
                                            onClick={() => setHiddenColumns({})}
                                        >
                                            Show All
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {showFilterButton && (
                        <button 
                            className={`cgrid-filter-btn ${activeFilterCount > 0 ? 'has-filters' : ''}`}
                            onClick={() => setAdvancedSearchOpen(true)}
                        >
                            <FilterListIcon />
                            <span>Set Filters</span>
                            {activeFilterCount > 0 && (
                                <span className="cgrid-filter-badge">{activeFilterCount}</span>
                            )}
                        </button>
                    )}
                    {activeFilterCount > 0 && (
                        <button
                            className="cgrid-reset-btn"
                            onClick={handleResetAllFilters}
                        >
                            Reset Filters
                        </button>
                    )}
                    {onSaveDefaults && (
                        <div className="cgrid-defaults-actions">
                            <Tooltip title="Save filter preferences">
                                <button
                                    className="cgrid-defaults-btn"
                                    onClick={() => onSaveDefaults({ filters, sortModel, hiddenColumns, pageCount })}
                                >
                                    <SaveIcon fontSize="small" />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                     {title && (
                        <h2 className="cgrid-title">{title}</h2>
                    )}
                </div>
            )}
            
            {/* Unified Filter Dialog */}
            {effectiveFields.length > 0 && (
                <AdvancedSearchDialog
                    open={advancedSearchOpen}
                    onClose={() => setAdvancedSearchOpen(false)}
                    fields={effectiveFields}
                    defaultValues={isAutoGenerated ? autoFilterValues : defaultAdvancedSearchValues}
                    onApply={(formData) => {
                        if (isAutoGenerated) {
                            handleAutoFilterApply(formData);
                        } else if (onAdvancedSearchApply) {
                            onAdvancedSearchApply(formData);
                        }
                        setAdvancedSearchOpen(false);
                    }}
                    onReset={() => {
                        if (isAutoGenerated) {
                            handleFiltersReset();
                        } else if (onAdvancedSearchApply) {
                            onAdvancedSearchApply({});
                        }
                    }}
                />
            )}
            
            {/* Table */}
            <div className="cgrid-table-wrapper">
                <table className="cgrid-table">
                    <thead>
                        <tr className="cgrid-header-row">
                            {visibleColumns.map((column, colIndex) => {
                                const sortDir = getSortDirection(column.mapping);
                                const isSortable = column.sortable !== false;
                                const frozenStyle = getFrozenColumnStyle(column, true);
                                const isFirstFrozen = column.frozen && colIndex === 0;
                                const filterInfo = getColumnFilterInfo(column.mapping);
                                const hasFilter = !!filterInfo;
                                
                                return (
                                    <Tooltip
                                        key={column.mapping}
                                        title={hasFilter ? filterInfo : ''} 
                                        arrow 
                                        placement="top"
                                        classes={{ popper: 'cgrid-filter-tooltip' }}
                                        disableHoverListener={!hasFilter}
                                    >
                                        <th
                                            data-column={column.mapping}
                                            className={`cgrid-header-cell ${isSortable ? 'sortable' : ''} ${sortDir ? 'sorted' : ''} ${column.frozen ? 'frozen' : ''} ${column.frozen && !isFirstFrozen ? 'frozen-border-left' : ''} ${hasFilter ? 'has-filter' : ''}`}
                                            style={{
                                                width: getColumnWidth(column),
                                                minWidth: column.minWidth || 50,
                                                maxWidth: column.frozen ? getColumnWidth(column) : (columnWidths[column.mapping] || column.maxWidth || undefined),
                                                textAlign: column.headerAlign || 'center',
                                                ...frozenStyle
                                            }}
                                            onClick={() => isSortable && handleSort(column)}
                                            onMouseEnter={() => setHoveredColumn(column.mapping)}
                                            onMouseLeave={() => setHoveredColumn(null)}
                                        >
                                            <div className="cgrid-header-content">
                                                {hasFilter && (
                                                    <FilterAltIcon className="cgrid-filter-indicator" />
                                                )}
                                                <span className="cgrid-header-title">{column.name}</span>
                                                {renderSortIcon(column)}
                                            </div>
                                            <div
                                                className="cgrid-resize-handle"
                                                onMouseDown={(e) => handleResizeStart(e, column)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </th>
                                    </Tooltip>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="cgrid-body">
                        {processedRows.length === 0 ? (
                            <tr className="cgrid-no-data-row">
                                <td colSpan={visibleColumns.length} className="cgrid-no-data-cell">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            processedRows.map((row, rowIndex) => {
                                const isActiveRow = activeRowId && (row.requestId === activeRowId || row.id === activeRowId);
                                return (
                                <tr
                                    key={row.id || rowIndex}
                                    className={`cgrid-row ${isActiveRow ? 'active-row' : ''} ${getRowClassName ? getRowClassName({ row }) : ''}`}
                                    onClick={() => onCellClick && onCellClick({ row })}
                                >
                                    {visibleColumns.map((column, colIndex) => {
                                        const cellClass = getCellClass(column, row);
                                        const cellContent = renderCell(column, row, rowIndex);
                                        const tooltipColumn = column.displayTootip;
                                        const frozenStyle = getFrozenColumnStyle(column, false, isActiveRow);
                                        const isFirstFrozen = column.frozen && colIndex === 0;
                                        
                                        return (
                                            <td
                                                key={column.mapping}
                                                data-column={column.mapping}
                                                className={`cgrid-cell ${cellClass} ${column.frozen ? 'frozen' : ''} ${column.frozen && !isFirstFrozen ? 'frozen-border-left' : ''} ${isActiveRow ? 'active-cell' : ''}`}
                                                style={{
                                                    width: getColumnWidth(column),
                                                    minWidth: column.minWidth || 50,
                                                    maxWidth: column.frozen ? getColumnWidth(column) : (columnWidths[column.mapping] || column.maxWidth || undefined),
                                                    textAlign: column.align || 'left',
                                                    ...frozenStyle
                                                }}
                                            >
                                                {tooltipColumn ? (
                                                    <Tooltip title={row[tooltipColumn[0]] || ''}>
                                                        <span className="cgrid-cell-content">
                                                            {cellContent}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <span className="cgrid-cell-content">
                                                        {cellContent}
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Pagination */}
            {showPagination && totalResults > 0 && (
                <div className="cgrid-pagination">
                    <div className="cgrid-pagination-info">
                        Showing {((currentPage - 1) * pageCount) + 1} to {Math.min(currentPage * pageCount, totalResults)} of {totalResults} records
                    </div>
                    <div className="cgrid-pagination-controls">
                        <div className="cgrid-page-size">
                            <label>Records per Page:</label>
                            <select 
                                value={pageCount} 
                                onChange={(e) => onPageCountChange && onPageCountChange(parseInt(e.target.value))}
                            >
                                {pageSizeOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <Tooltip title="First Page">
                            <span>
                                <IconButton 
                                    onClick={() => onPageChange && onPageChange(1)} 
                                    disabled={currentPage === 1}
                                    className="cgrid-pagination-btn"
                                >
                                    <FirstPageIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Previous Page">
                            <span>
                                <IconButton 
                                    onClick={() => onPageChange && onPageChange(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                    className="cgrid-pagination-btn"
                                >
                                    <ChevronLeftIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <span className="cgrid-pagination-pages">
                            Page {currentPage} of {Math.ceil(totalResults / pageCount)}
                        </span>
                        <div className="cgrid-goto-page">
                            <label>Go to:</label>
                            <Tooltip 
                                title="Page doesn't exist" 
                                open={gotoPageError}
                                arrow
                                PopperProps={{
                                    disablePortal: true,
                                }}
                            >
                                <input
                                    type="text"
                                    value={gotoPageValue}
                                    onChange={handleGotoPageChange}
                                    onKeyPress={handleGotoPageSubmit}
                                    placeholder="#"
                                    className={`cgrid-goto-page-input ${gotoPageError ? 'error' : ''}`}
                                />
                            </Tooltip>
                        </div>
                        <Tooltip title="Next Page">
                            <span>
                                <IconButton 
                                    onClick={() => onPageChange && onPageChange(currentPage + 1)} 
                                    disabled={currentPage >= Math.ceil(totalResults / pageCount)}
                                    className="cgrid-pagination-btn"
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Last Page">
                            <span>
                                <IconButton 
                                    onClick={() => onPageChange && onPageChange(Math.ceil(totalResults / pageCount))} 
                                    disabled={currentPage >= Math.ceil(totalResults / pageCount)}
                                    className="cgrid-pagination-btn"
                                >
                                    <LastPageIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomGrid;
export { applyFilter, getOperatorsForType };
